import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Columns3 } from "lucide-react";
import { format, isValid, parseISO } from "date-fns";
import { useLocation } from "react-router-dom";
import { getAuthItem, setAuthItem } from "../../../public/utils/authStorage";
import { getJwtRoleSlug } from "../../../public/utils/jwtRole";
import { downloadMediaUrl } from "../../../utils/downloadMediaUrl";
import {
  createDonation,
  getAllDonations,
  getDonationById,
  getDonationPurposes,
  getDonationStats,
  patchDonationRecordStatus,
  bulkSetDonationVerified,
  downloadDonation80gCertificate,
  updateDonation,
} from "../api/donationsApi";
import {
  clearOnlinePaymentSession,
  clearPhonePePaymentResult,
  getDonationFormDraft,
  getPhonePePaymentResult,
  isActivePhonePePayment,
  markPhonePeOrderConsumed,
  setDonationFormDraft,
} from "../../payment/paymentSession";
import DonationPaymentStrip from "../components/DonationPaymentStrip";
import { getAllDonors } from "../api/donorsApi";
import { masterSelectOptions } from "../../memberships/utils/masterLookupUtils";
import { getCsrfToken } from "../../../public/api/authApi";
import GlobalQuickJump from "../../../global/GlobalQuickJump";
import GlobalFeedback from "../../../global/GlobalFeedback";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";
import GlobalMetricCards from "../../../global/GlobalMetricCards";
import GlobalDataTable from "../../../global/GlobalDataTable";
import GlobalBadge from "../../../global/GlobalBadge";
import { GlobalTableActionButton } from "../../../global/GlobalTableActionButton";
import { GlobalModal } from "../../../global/GlobalModal";
import {
  GlobalButton,
  GlobalDateField,
  GlobalSelectField,
  GlobalTextAreaField,
  GlobalTextField,
} from "../../../global/forms/GlobalFormFields";
import GlobalFormShell from "../../../global/forms/GlobalFormShell";
import { fetchCertificateTemplates } from "../../certificates/api/certificateTemplatesApi";
import DonationPurposesMaster from "../components/masters/DonationPurposesMaster";
import DonorEquipmentListPage from "./DonorEquipmentListPage";
import "../../user-builder/styles/userBuilder.css";
import "../../user-builder/styles/UserBuilderPage.css";
import "../styles/VolunteerTable.css";
import "../styles/tournamentRegistration.css";
import "../styles/MembershipApplications.css";

const ALERT_ID = "donation-form-alert";
const PAYMENT_REFERENCE_MAX_LEN = 30;
const COLUMN_VISIBILITY_KEY = "girinestham.donationList.columnVisibility";
const COLUMN_DEFS = [
  { id: "donation_receipt_no", label: "Donation ID", defaultVisible: true },
  { id: "donor_name", label: "Donor name", defaultVisible: true },
  { id: "donation_amount", label: "Amount", defaultVisible: true },
  { id: "donation_verified", label: "Donation verified", defaultVisible: true },
  { id: "certificate_template_name", label: "Certificate template", defaultVisible: true },
  { id: "donation_date", label: "Date", defaultVisible: true },
  { id: "donation_mode", label: "Mode", defaultVisible: true },
  { id: "payment_type", label: "Payment type", defaultVisible: true },
  { id: "payment_reference", label: "Receipt / Transaction no", defaultVisible: true },
  { id: "payment_remarks", label: "Payment remarks", defaultVisible: true },
  { id: "donation_purpose", label: "Purpose", defaultVisible: true },
  { id: "payment_verified", label: "Payment verified", defaultVisible: true },
];

const EMPTY_FORM = () => ({
  donor_id: "",
  donor_name_input: "",
  donation_amount: "",
  donation_date: format(new Date(), "yyyy-MM-dd"),
  purpose_id: "",
  payment_type: "offline",
  payment_transaction_id: "",
  payment_proof: "",
  payment_proof_file_name: "",
  remarks: "",
});

function safeErrorText(err, fallback = "Something went wrong. Please try again.") {
  const m = err?.message;
  return typeof m === "string" && m.trim() ? m : fallback;
}
function formatMoney(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  return Number.isFinite(n)
    ? `₹${new Intl.NumberFormat("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n)}`
    : "—";
}
function paymentVerifiedLabel(row) {
  return Number(row?.payment_verified) === 1 ? "Verified" : "Not verified";
}
function donationVerifiedLabel(row) {
  return Number(row?.donation_verified) === 1 ? "Verified" : "Not verified";
}
function formatDateCell(value) {
  if (value == null || value === "") return "—";
  try {
    const raw = String(value).trim();
    let d = parseISO(raw);
    if (!isValid(d) && /^\d{4}-\d{2}-\d{2}/.test(raw)) d = new Date(raw.replace(" ", "T"));
    if (!isValid(d)) d = new Date(raw);
    return isValid(d) ? format(d, "PP") : "—";
  } catch {
    return "—";
  }
}
function dateInputValue(value) {
  if (!value) return "";
  const s = String(value);
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : "";
}
function isPendingRow(row) {
  return String(row.record_status_name || "").toLowerCase() === "pending_approval";
}
function isCancelledRow(row) {
  const n = String(row.record_status_name || "").toLowerCase();
  return n === "inactive" || Number(row.record_status_id) === 2;
}
function loadSavedVisibility() {
  try {
    const raw = localStorage.getItem(COLUMN_VISIBILITY_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore
  }
  return null;
}

function escapeCsvCell(v) {
  const s = v == null ? "" : String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, "\"\"")}"`;
  return s;
}

function exportDonationsCsv(rows, columns) {
  const exportColumns = (columns || []).filter(Boolean);
  const headers = exportColumns.map((c) => c.label);
  const lines = [headers.join(",")];
  for (const row of rows || []) {
    const vals = exportColumns.map((c) => {
      if (c.id === "donation_amount") return escapeCsvCell(row.donation_amount ?? "");
      if (c.id === "donation_date") return escapeCsvCell(row.donation_date ?? "");
      if (c.id === "donation_verified") return escapeCsvCell(donationVerifiedLabel(row));
      if (c.id === "payment_reference") return escapeCsvCell(row.payment_transaction_id || row.payment_receipt_no || "");
      if (c.id === "payment_verified") return escapeCsvCell(paymentVerifiedLabel(row));
      return escapeCsvCell(row[c.id] ?? "");
    });
    lines.push(vals.join(","));
  }
  const blob = new Blob(["\ufeff", lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const today = new Date();
  const y = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, "0");
  const d = String(today.getDate()).padStart(2, "0");
  a.download = `donations-${y}-${m}-${d}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function sanitizeDonationAmountInput(raw) {
  let s = String(raw ?? "").replace(/[^\d.]/g, "");
  const dot = s.indexOf(".");
  if (dot !== -1) {
    s = `${s.slice(0, dot + 1)}${s.slice(dot + 1).replace(/\./g, "")}`;
  }
  return s;
}

function isStrictPositiveAmountString(value) {
  const t = String(value ?? "").trim();
  if (!t) return false;
  if (!/^\d+(\.\d*)?$/.test(t)) return false;
  const n = Number(t);
  return Number.isFinite(n) && n > 0;
}

function validateForm(formData) {
  const errors = {};
  if (!formData.donor_id) errors.donor_name_input = "Please select a donor from suggestions";
  const amountStr = String(formData.donation_amount ?? "").trim();
  if (!amountStr) {
    errors.donation_amount = "Amount is required";
  } else if (!isStrictPositiveAmountString(amountStr)) {
    errors.donation_amount =
      "Use digits only (optional decimal). Amount must be greater than zero; negatives are not allowed.";
  }
  if (!formData.donation_date) errors.donation_date = "Donation date is required";
  if (!formData.purpose_id) errors.purpose_id = "Donation purpose is required";
  const phonePe = getPhonePePaymentResult();
  const payingOnline =
    formData.payment_type === "online" || isActivePhonePePayment(phonePe);

  if (payingOnline) {
    const hasProof = Boolean(String(formData.payment_proof || "").trim());
    if (!isActivePhonePePayment(phonePe) && !hasProof) {
      errors.phonepe = "Complete online payment or upload payment proof before saving";
    }
  } else {
    const ref = String(formData.payment_transaction_id || "").trim();
    if (!ref) {
      errors.payment_transaction_id = "Receipt number is required";
    } else if (ref.length > PAYMENT_REFERENCE_MAX_LEN) {
      errors.payment_transaction_id = `At most ${PAYMENT_REFERENCE_MAX_LEN} characters allowed`;
    }
  }
  if (!String(formData.payment_proof || "").trim()) {
    errors.payment_proof = "Upload payment proof before saving";
  }
  return errors;
}

export default function DonationsControlPage() {
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [donations, setDonations] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [donors, setDonors] = useState([]);
  const [purposes, setPurposes] = useState([]);
  const [phonePeCompleted, setPhonePeCompleted] = useState(() => {
    const phonePe = getPhonePePaymentResult();
    return isActivePhonePePayment(phonePe) ? phonePe : null;
  });
  const [formData, setFormData] = useState(() => EMPTY_FORM());
  const [editing, setEditing] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [formErrorDetails, setFormErrorDetails] = useState({});
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [activeQuickJump, setActiveQuickJump] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmType, setConfirmType] = useState(null);
  const [donationToHandle, setDonationToHandle] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [searchDonorName, setSearchDonorName] = useState("");
  const [filterColumn, setFilterColumn] = useState("");
  const [filterValue, setFilterValue] = useState("");
  const [filterFromDate, setFilterFromDate] = useState("");
  const [filterToDate, setFilterToDate] = useState("");
  const [columnPickerOpen, setColumnPickerOpen] = useState(false);
  const [selectedDonationIds, setSelectedDonationIds] = useState(new Set());
  const [bulkMarking, setBulkMarking] = useState(false);
  const [certificateTemplates, setCertificateTemplates] = useState([]);
  const [certTemplatePick, setCertTemplatePick] = useState("");
  const [downloading80gId, setDownloading80gId] = useState(null);
  const [downloadingReceiptId, setDownloadingReceiptId] = useState(null);
  const [phonePeError, setPhonePeError] = useState("");
  const location = useLocation();
  const pickerRef = useRef(null);
  const autoSaveAttemptedRef = useRef(false);
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = loadSavedVisibility();
    const initial = {};
    for (const c of COLUMN_DEFS) initial[c.id] = saved?.[c.id] ?? c.defaultVisible;
    return initial;
  });

  const isEditMode = Boolean(editing?.donation_id);
  const roleSlug = getJwtRoleSlug();
  const isCollectDonationsRoute = location.pathname.includes("/collect-donations");
  const isDonationTrackingRoute = location.pathname.includes("/donation-tracking");
  // Tracking pages hide the add form by default; show it while editing so Edit matches admin behavior.
  const showDonationForm = !isDonationTrackingRoute || Boolean(editing?.donation_id);
  const showDonationTable = !isCollectDonationsRoute;
  const showDonationMasters =
    location.pathname.endsWith("/donations") ||
    location.pathname.endsWith("/donor-support/donations");
  const isAdminRoute = showDonationMasters;
  const isAdminUser = roleSlug === "admin";
  const isStaffDonationTracking = isDonationTrackingRoute && !isAdminUser;
  const showEquipmentCollectEmbed = isCollectDonationsRoute && !isAdminUser;
  const showEquipmentTrackingEmbed = isDonationTrackingRoute && !isAdminUser;
  const activeDonors = useMemo(
    () => (donors || []).filter((d) => Number(d.is_active) === 1),
    [donors],
  );
  const donorSuggestionList = useMemo(() => {
    const q = String(formData.donor_name_input || "").trim().toLowerCase();
    if (!q) return activeDonors.slice(0, 8);
    return activeDonors
      .filter((d) => String(d.donor_name || "").toLowerCase().includes(q) || String(d.donor_code || "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [activeDonors, formData.donor_name_input]);
  const purposeOptions = useMemo(
    () =>
      masterSelectOptions(purposes, {
        labelKey: "purpose_name",
        selectedValue: formData.purpose_id,
      }).map((o) => ({ value: String(o.value), label: o.label })),
    [purposes, formData.purpose_id],
  );
  useEffect(() => {
    const phonePe = getPhonePePaymentResult();
    const activePhonePe = isActivePhonePePayment(phonePe) ? phonePe : null;
    const draft = getDonationFormDraft();
    if (draft || activePhonePe) {
      setFormData((prev) => ({
        ...prev,
        ...(draft || {}),
        payment_type:
          activePhonePe || draft?.payment_type === "online" ? "online" : prev.payment_type,
      }));
    }
    setPhonePeCompleted(activePhonePe);
  }, [location.pathname]);
  const visibleDataColumns = useMemo(() => COLUMN_DEFS.filter((c) => columnVisibility[c.id]), [columnVisibility]);
  const tableHeaders = useMemo(
    () => [
      ...(isAdminRoute ? [{ key: "select", label: "", className: "checkbox-cell" }] : []),
      ...visibleDataColumns.map((c) => ({ key: c.id, label: c.label })),
      { key: "actions", label: "Actions", className: "ub-table__col-actions" },
    ],
    [visibleDataColumns, isAdminRoute],
  );
  const columnFilterOptions = useMemo(
    () => COLUMN_DEFS.map((c) => ({ value: c.id, label: c.label })),
    [],
  );

  const valueFilterOptions = useMemo(() => {
    if (!filterColumn) return [];
    const values = [
      ...new Set(
        (donations || []).map((row) => {
          if (filterColumn === "payment_reference") return row.payment_transaction_id || row.payment_receipt_no || "";
          if (filterColumn === "donation_verified") return donationVerifiedLabel(row);
          if (filterColumn === "payment_verified") return paymentVerifiedLabel(row);
          return row?.[filterColumn] ?? "";
        }),
      ),
    ]
      .map((v) => String(v).trim())
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
    return values.map((v) => ({ value: v, label: v }));
  }, [donations, filterColumn]);

  const filteredRows = useMemo(() => {
    return donations.filter((row) => {
      const donorQ = String(searchDonorName || "").trim().toLowerCase();
      if (donorQ && !String(row.donor_name || "").toLowerCase().includes(donorQ)) return false;
      if (filterFromDate) {
        const d = String(row.donation_date || "").slice(0, 10);
        if (d < filterFromDate) return false;
      }
      if (filterToDate) {
        const d = String(row.donation_date || "").slice(0, 10);
        if (d > filterToDate) return false;
      }
      if (filterColumn && filterValue) {
        let current = "";
        if (filterColumn === "payment_reference") current = row.payment_transaction_id || row.payment_receipt_no || "";
        else if (filterColumn === "donation_verified") current = donationVerifiedLabel(row);
        else if (filterColumn === "payment_verified") current = paymentVerifiedLabel(row);
        else current = row?.[filterColumn] ?? "";
        if (String(current).trim() !== String(filterValue).trim()) return false;
      }
      return true;
    });
  }, [donations, searchDonorName, filterColumn, filterValue, filterFromDate, filterToDate]);

  const loadLookups = useCallback(async () => {
    try {
      const [donorsRes, purposesRes] = await Promise.all([
        getAllDonors(),
        getDonationPurposes(),
      ]);
      setDonors(donorsRes?.data || []);
      setPurposes(purposesRes?.data || []);
    } catch (err) {
      console.error(err);
    }
  }, []);
  const refreshStats = useCallback(async () => {
    try {
      const res = await getDonationStats();
      setStats(res?.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingStats(false);
    }
  }, []);
  const refreshList = useCallback(async () => {
    setLoadingList(true);
    setErrorMsg("");
    try {
      const res = await getAllDonations();
      setDonations(res?.data || []);
    } catch (err) {
      setErrorMsg(safeErrorText(err));
    } finally {
      setLoadingList(false);
    }
  }, []);

  const handleEquipmentDataChanged = useCallback(() => {
    refreshStats();
  }, [refreshStats]);

  useEffect(() => {
    loadLookups();
    refreshStats();
    refreshList();
  }, [loadLookups, refreshStats, refreshList]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!pickerRef.current?.contains(e.target)) setColumnPickerOpen(false);
    };
    if (columnPickerOpen) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [columnPickerOpen]);

  const setFormField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrorDetails[name]) setFormErrorDetails((prev) => ({ ...prev, [name]: "" }));
  };

  const onSelectDonorSuggestion = (donor) => {
    setFormData((prev) => ({
      ...prev,
      donor_id: String(donor.id),
      donor_name_input: `${donor.donor_name} (${donor.donor_code || donor.id})`,
    }));
    setFormErrorDetails((prev) => ({ ...prev, donor_name_input: "" }));
  };

  const saveDonationFromForm = useCallback(
    async (data, { isAuto = false } = {}) => {
      const errors = validateForm(data);
      setFormErrorDetails(errors);
      setFormError("");
      if (Object.keys(errors).length > 0) {
        if (!isAuto) setFormError("Please check the highlighted fields below.");
        return false;
      }
      setFormSubmitting(true);
      try {
        let csrfToken = localStorage.getItem("csrf-token");
        if (!csrfToken) {
          csrfToken = await getCsrfToken();
          if (csrfToken) localStorage.setItem("csrf-token", csrfToken);
        }
        const phonePe = getPhonePePaymentResult();
        const paymentType =
          data.payment_type === "online" || isActivePhonePePayment(phonePe)
            ? "online"
            : "offline";

        const body = {
          donor_id: Number(data.donor_id),
          donation_amount: Number(data.donation_amount),
          donation_date: data.donation_date,
          purpose_id: Number(data.purpose_id),
          remarks: data.remarks?.trim() || undefined,
          payment: {
            payment_type: paymentType,
            transaction_id:
              paymentType === "offline"
                ? data.payment_transaction_id?.trim()
                : undefined,
            phonepe: paymentType === "online" ? phonePe : undefined,
            proof: data.payment_proof?.trim() || null,
            remarks: data.remarks?.trim() || null,
          },
        };
        if (isEditMode) {
          await updateDonation(editing.donation_id, body, csrfToken);
          setSuccessMsg("Donation updated successfully.");
        } else {
          await createDonation(body, csrfToken);
          setSuccessMsg("Donation recorded successfully.");
        }
        if (phonePe?.orderId) {
          markPhonePeOrderConsumed(phonePe.orderId);
        }
        setEditing(null);
        setFormData(EMPTY_FORM());
        setFormErrorDetails({});
        setFormError("");
        clearOnlinePaymentSession();
        setPhonePeCompleted(null);
        autoSaveAttemptedRef.current = false;
        refreshStats();
        refreshList();
        return true;
      } catch (err) {
        const d = err?.details;
        if (d && typeof d === "object") {
          const next = {};
          if (typeof d.transaction_id === "string" && d.transaction_id.trim()) {
            next.payment_transaction_id = d.transaction_id;
          }
          if (typeof d.phonepe === "string" && d.phonepe.trim()) {
            next.phonepe = d.phonepe;
          }
          if (typeof d.amount === "string" && d.amount.trim()) {
            next.donation_amount = d.amount;
          }
          if (typeof d.proof === "string" && d.proof.trim()) {
            next.payment_proof = d.proof;
          }
          if (Object.keys(next).length) {
            setFormErrorDetails((prev) => ({ ...prev, ...next }));
          }
        }
        setFormError(safeErrorText(err));
        return false;
      } finally {
        setFormSubmitting(false);
      }
    },
    [isEditMode, editing, refreshStats, refreshList],
  );

  const onSubmit = async (e) => {
    e.preventDefault();
    await saveDonationFromForm(formData);
  };

  useEffect(() => {
    if (!isCollectDonationsRoute || isEditMode || editing) return;
    if (autoSaveAttemptedRef.current || formSubmitting) return;

    const phonePe = getPhonePePaymentResult();
    const draft = getDonationFormDraft();
    if (!isActivePhonePePayment(phonePe) || !draft?.donor_id) return;

    const merged = {
      ...EMPTY_FORM(),
      ...draft,
      payment_type: "online",
    };

    autoSaveAttemptedRef.current = true;
    setFormData(merged);
    setPhonePeCompleted(phonePe);

    void saveDonationFromForm(merged, { isAuto: true }).then((ok) => {
      if (!ok) autoSaveAttemptedRef.current = false;
    });
  }, [
    isCollectDonationsRoute,
    isEditMode,
    editing,
    formSubmitting,
    saveDonationFromForm,
    location.pathname,
  ]);

  const onRequestEdit = async (row) => {
    setFormError("");
    try {
      const res = await getDonationById(row.donation_id);
      const d = res?.data;
      if (!d) return;
      const donor = donors.find((x) => Number(x.id) === Number(d.donor_id));
      setEditing({ donation_id: d.id });
      setFormData({
        donor_id: String(d.donor_id),
        donor_name_input: donor ? `${donor.donor_name} (${donor.donor_code || donor.id})` : (d.donor_name || ""),
        donation_amount: String(d.donation_amount ?? ""),
        donation_date: dateInputValue(d.donation_date) || format(new Date(), "yyyy-MM-dd"),
        purpose_id: d.purpose_id != null ? String(d.purpose_id) : "",
        payment_type: d.payment_type === "online" ? "online" : "offline",
        payment_transaction_id: d.payment_transaction_id || "",
        payment_proof: d.payment_proof || "",
        remarks: d.remarks ?? "",
      });
      requestAnimationFrame(() => {
        document.getElementById("donation-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } catch (err) {
      setErrorMsg(safeErrorText(err));
    }
  };

  const openConfirm = (row, type) => {
    setDonationToHandle(row);
    setConfirmType(type);
    setShowConfirmModal(true);
  };
  const handleConfirm = async () => {
    if (!donationToHandle || !confirmType) return;
    setIsConfirming(true);
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        if (csrfToken) localStorage.setItem("csrf-token", csrfToken);
      }
      await patchDonationRecordStatus(donationToHandle.donation_id, confirmType === "approve" ? "approve" : "cancel", csrfToken);
      setSuccessMsg(confirmType === "approve" ? "Donation approved successfully." : "Donation deleted.");
      setShowConfirmModal(false);
      refreshStats();
      refreshList();
    } catch (err) {
      setErrorMsg(safeErrorText(err));
    } finally {
      setIsConfirming(false);
    }
  };

  const toggleColumn = (id) => {
    setColumnVisibility((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      const visibleCount = COLUMN_DEFS.filter((c) => next[c.id]).length;
      if (visibleCount === 0) return prev;
      try {
        localStorage.setItem(COLUMN_VISIBILITY_KEY, JSON.stringify(next));
      } catch {
        // ignore
      }
      return next;
    });
  };

  const clearTableFilters = () => {
    setSearchDonorName("");
    setFilterColumn("");
    setFilterValue("");
    setFilterFromDate("");
    setFilterToDate("");
  };
  const toggleDonationSelection = (rowId, checked) => {
    const key = String(rowId);
    setSelectedDonationIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  };
  const certTemplateOptions = useMemo(
    () =>
      (certificateTemplates || []).map((t) => ({
        value: String(t.id),
        label: t.purpose_name || `Template ${t.id}`,
      })),
    [certificateTemplates],
  );

  useEffect(() => {
    if (!isAdminRoute) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchCertificateTemplates();
        if (!cancelled) setCertificateTemplates(res?.data || []);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdminRoute]);

  const runBulkDonationVerified = async (verified) => {
    const ids = Array.from(selectedDonationIds).map((id) => Number(id)).filter((n) => Number.isInteger(n) && n > 0);
    if (ids.length === 0) return;
    if (verified && !certTemplatePick) {
      setErrorMsg("Choose a certificate template before verifying donations.");
      return;
    }
    setBulkMarking(true);
    setErrorMsg("");
    try {
      const csrf = await withCsrf();
      await bulkSetDonationVerified(ids, verified, csrf, verified ? certTemplatePick : undefined);
      setSuccessMsg(verified ? "Selected donations verified with certificate template." : "Selected donations marked as not verified.");
      setSelectedDonationIds(new Set());
      if (verified) setCertTemplatePick("");
      await refreshList();
    } catch (err) {
      setErrorMsg(safeErrorText(err, "Failed to update donation verification."));
    } finally {
      setBulkMarking(false);
    }
  };

  const withCsrf = async () => {
    let csrfToken = getAuthItem("csrf-token") || getAuthItem("csrf_token");
    if (!csrfToken) {
      csrfToken = await getCsrfToken();
      if (csrfToken) {
        setAuthItem("csrf-token", csrfToken);
        setAuthItem("csrf_token", csrfToken);
      }
    }
    return csrfToken;
  };

  const onDownloadReceipt = async (row) => {
    const proof = row?.payment_proof || row?.proof;
    if (!proof) {
      setErrorMsg("No payment proof uploaded for this donation.");
      return;
    }
    const id = row?.donation_id;
    setErrorMsg("");
    setDownloadingReceiptId(id);
    try {
      const filename =
        row?.payment_proof_file_name ||
        `payment-proof-${row?.payment_transaction_id || row?.donation_receipt_no || id}`;
      await downloadMediaUrl(proof, filename);
    } catch (err) {
      setErrorMsg(safeErrorText(err, "Failed to download payment proof."));
    } finally {
      setDownloadingReceiptId(null);
    }
  };

  const onDownload80g = async (row) => {
    const id = Number(row?.donation_id);
    if (!Number.isFinite(id) || id < 1) return;
    setErrorMsg("");
    setDownloading80gId(id);
    try {
      const csrf = await withCsrf();
      await downloadDonation80gCertificate(id, csrf);
      setSuccessMsg("80G certificate downloaded.");
    } catch (err) {
      setErrorMsg(safeErrorText(err, "Failed to generate 80G certificate."));
    } finally {
      setDownloading80gId(null);
    }
  };

  return (
    <div className="user-builder-shell">
      <header className="user-builder-hero">
        <div className="user-builder-hero__text">
          <p className="user-builder-hero__eyebrow">Administrative Area</p>
          <h2 className="user-builder-hero__title">Donations control</h2>
        </div>
      </header>
      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />
      <GlobalFeedback type="error" text={errorMsg} />
      <div className="user-builder-layout">
        <div className="user-builder-layout__main">
          <div id="donation-stats" className="user-builder-panel" style={{ scrollMarginTop: "2rem" }}>
            <div className="user-builder-panel__head"><h3 className="user-builder-panel__title">Cash donations</h3></div>
            <GlobalMetricCards
              items={[
                { key: "total", label: "Total (cash)", value: formatMoney(stats?.total_donations), tone: "teal" },
                { key: "monthly", label: "This month (cash)", value: formatMoney(stats?.monthly_donations), tone: "slate" },
                { key: "online", label: "Online (cash)", value: formatMoney(stats?.online_donations), tone: "mint" },
                { key: "offline", label: "Offline (cash)", value: formatMoney(stats?.offline_donations), tone: "coral" },
              ]}
              loading={loadingStats}
              ariaLabel="Cash donation totals"
            />
            <div className="user-builder-panel__head" style={{ marginTop: "1.25rem" }}><h3 className="user-builder-panel__title">Equipment donations (estimated value)</h3></div>
            <GlobalMetricCards
              items={[
                { key: "eq_total", label: "Total equipment value", value: formatMoney(stats?.total_equipment_value), tone: "mint" },
                { key: "eq_month", label: "This month (equipment)", value: formatMoney(stats?.monthly_equipment_value), tone: "coral" },
              ]}
              loading={loadingStats}
              ariaLabel="Equipment donation value totals"
            />
          </div>

          {showDonationForm ? (
          <div id="donation-form" className="user-builder-panel" style={{ scrollMarginTop: "2rem" }}>
            <div className="user-builder-panel__head"><h3 className="user-builder-panel__title">{isEditMode ? "Edit donation" : "Add donation"}</h3></div>
            <form onSubmit={onSubmit} noValidate>
              <GlobalFormShell legend={isEditMode ? "Donation" : "New record"} showAlert={Boolean(formError)} alertText={formError} alertId={ALERT_ID}
                actions={<div style={{ display: "flex", gap: "0.5rem" }}>
                  <GlobalButton type="submit" variant="primary" disabled={formSubmitting}>{formSubmitting ? "Saving…" : isEditMode ? "Save changes" : "Save donation"}</GlobalButton>
                  {isEditMode ? <GlobalButton type="button" variant="ghost" onClick={() => { setEditing(null); setFormData(EMPTY_FORM()); setFormError(""); setFormErrorDetails({}); }}>Cancel edit</GlobalButton> : null}
                </div>}>
                <div className="gff-field gff-field--full" style={{ position: "relative" }}>
                  <GlobalTextField
                    label="Donor name"
                    value={formData.donor_name_input}
                    onChange={(v) => {
                      setFormData((p) => ({ ...p, donor_name_input: v, donor_id: "" }));
                      if (formErrorDetails.donor_name_input) setFormErrorDetails((p) => ({ ...p, donor_name_input: "" }));
                    }}
                    error={formErrorDetails.donor_name_input}
                    baseAlertId={ALERT_ID}
                    placeholder="Type donor name/code"
                    disabled={formSubmitting}
                  />
                  {formData.donor_name_input && donorSuggestionList.length > 0 ? (
                    <div style={{ position: "absolute", zIndex: 30, left: 0, right: 0, top: "100%", background: "#fff", border: "1px solid #e2e8f0", borderRadius: "8px", maxHeight: "180px", overflowY: "auto" }}>
                      {donorSuggestionList.map((d) => (
                        <button key={d.id} type="button" onClick={() => onSelectDonorSuggestion(d)} style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 10px", border: 0, background: "#fff" }}>
                          {d.donor_name} ({d.donor_code || d.id})
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
                <GlobalTextField
                  label="Amount (₹)"
                  value={formData.donation_amount}
                  onChange={(v) => setFormField("donation_amount", sanitizeDonationAmountInput(v))}
                  error={formErrorDetails.donation_amount}
                  baseAlertId={ALERT_ID}
                  inputMode="decimal"
                  required
                />
                <GlobalDateField label="Donation date" value={formData.donation_date} onChange={(v) => setFormField("donation_date", v)} error={formErrorDetails.donation_date} baseAlertId={ALERT_ID} required />
                <GlobalSelectField label="Donation purpose" value={formData.purpose_id} onChange={(v) => setFormField("purpose_id", v)} options={purposeOptions} placeholderOption="—" error={formErrorDetails.purpose_id} baseAlertId={ALERT_ID} required />
                <DonationPaymentStrip
                  paymentType={
                    isActivePhonePePayment(phonePeCompleted) ? "online" : formData.payment_type
                  }
                  onBeforePayRedirect={() => {
                    autoSaveAttemptedRef.current = false;
                    clearPhonePePaymentResult();
                    setPhonePeCompleted(null);
                    setDonationFormDraft({
                      ...formData,
                      payment_type: "online",
                    });
                  }}
                  onClearOnlinePayment={() => {
                    autoSaveAttemptedRef.current = false;
                    clearOnlinePaymentSession();
                    setPhonePeCompleted(null);
                    setPhonePeError("");
                    setFormErrorDetails((prev) => ({ ...prev, phonepe: "" }));
                  }}
                  onPaymentTypeChange={(type) => {
                    setFormField("payment_type", type);
                    setPhonePeError("");
                    if (type === "online") {
                      setFormField("payment_transaction_id", "");
                      if (formErrorDetails.payment_transaction_id) {
                        setFormErrorDetails((prev) => ({ ...prev, payment_transaction_id: "" }));
                      }
                    } else if (formErrorDetails.phonepe) {
                      setFormErrorDetails((prev) => ({ ...prev, phonepe: "" }));
                    }
                  }}
                  receiptValue={formData.payment_transaction_id}
                  onReceiptChange={(v) =>
                    setFormField("payment_transaction_id", String(v ?? "").slice(0, PAYMENT_REFERENCE_MAX_LEN))
                  }
                  receiptError={formErrorDetails.payment_transaction_id}
                  amount={formData.donation_amount}
                  phonePeCompleted={phonePeCompleted}
                  phonePeErrorMsg={phonePeError}
                  onPhonePeError={setPhonePeError}
                  formErrorDetails={formErrorDetails}
                  formSubmitting={formSubmitting}
                  alertId={ALERT_ID}
                  proofUrl={formData.payment_proof}
                  proofFileName={formData.payment_proof_file_name}
                  proofError={formErrorDetails.payment_proof || formErrorDetails.proof}
                  onProofChange={({ url, fileName }) => {
                    setFormData((p) => ({
                      ...p,
                      payment_proof: url || "",
                      payment_proof_file_name: fileName || "",
                    }));
                    setFormErrorDetails((prev) => {
                      const next = { ...prev };
                      delete next.payment_proof;
                      delete next.proof;
                      return next;
                    });
                  }}
                />
                <GlobalTextAreaField label="Remarks" value={formData.remarks} onChange={(v) => setFormField("remarks", v)} rows={2} error={formErrorDetails.remarks} baseAlertId={ALERT_ID} />
              </GlobalFormShell>
            </form>
          </div>
          ) : null}

          {showEquipmentCollectEmbed ? (
            <div id="equipment-donations-embed" className="user-builder-panel" style={{ scrollMarginTop: "2rem" }}>
              <div className="user-builder-panel__head">
                <h3 className="user-builder-panel__title">Equipment donations</h3>
                <p className="user-builder-panel__hint">Record in-kind equipment from donors (same as admin donor equipment).</p>
              </div>
              <DonorEquipmentListPage embedMode="donations-collect" onEquipmentDataChanged={handleEquipmentDataChanged} />
            </div>
          ) : null}

          {showDonationTable ? (
          <div id="donation-list" className="user-builder-panel" style={{ scrollMarginTop: "2rem" }}>
            <div className="user-builder-panel__head"><h3 className="user-builder-panel__title">Donations</h3></div>
            <div className="ub-filters ub-directory-filters" style={{ display: "flex", flexDirection: "column", gap: "0.35rem", marginTop: 0, marginBottom: "0.5rem" }}>
              <div className="ub-ms-filter-row" style={{ display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "flex-end" }}>
              <div style={{ flex: "1 1 320px", minWidth: "220px" }}>
                <GlobalTextField label="Search by donor name" value={searchDonorName} onChange={setSearchDonorName} placeholder="Type donor name" />
              </div>
              <div style={{ flex: "0 1 200px", minWidth: "160px" }}>
                <GlobalSelectField
                  label="Column"
                  value={filterColumn}
                  onChange={(v) => {
                    setFilterColumn(v);
                    setFilterValue("");
                  }}
                  options={columnFilterOptions}
                  placeholderOption="Select"
                />
              </div>
              <div style={{ flex: "0 1 220px", minWidth: "160px" }}>
                <GlobalSelectField
                  label="Value"
                  value={filterValue}
                  onChange={setFilterValue}
                  options={valueFilterOptions}
                  placeholderOption="All"
                  disabled={!filterColumn}
                />
              </div>
              <GlobalDateField label="From date" value={filterFromDate} onChange={setFilterFromDate} />
              <GlobalDateField label="To date" value={filterToDate} onChange={setFilterToDate} />
              <div style={{ flex: "0 0 auto" }}>
                <GlobalButton type="button" size="sm" variant="ghost" onClick={clearTableFilters}>
                  Clear filter
                </GlobalButton>
              </div>
              </div>
              <div
                className="ub-directory-table-toolbar"
                style={{ display: "flex", alignItems: "center", gap: "0.65rem", marginBottom: "0.65rem", width: "100%" }}
              >
                <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", padding: "0.2rem 0" }}>
                  <div className="vt-column-picker" ref={pickerRef}>
                    <GlobalButton
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setColumnPickerOpen((o) => !o)}
                      aria-expanded={columnPickerOpen}
                      aria-controls="donation-list-column-panel"
                      style={{ width: "fit-content", whiteSpace: "nowrap" }}
                    >
                      <Columns3 size={16} aria-hidden />
                      <span style={{ marginLeft: "0.35rem" }}>Columns</span>
                    </GlobalButton>
                    {columnPickerOpen ? (
                      <div id="donation-list-column-panel" className="vt-column-picker__panel" role="group" aria-label="Choose visible columns">
                        <p className="vt-column-picker__title">Show columns</p>
                        <ul className="vt-column-picker__list">
                          {COLUMN_DEFS.map((c) => (
                            <li key={c.id}>
                              <label className="vt-column-picker__row">
                                <input type="checkbox" checked={!!columnVisibility[c.id]} onChange={() => toggleColumn(c.id)} />
                                <span>{c.label}</span>
                              </label>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                </div>
                <GlobalButton
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => exportDonationsCsv(filteredRows, visibleDataColumns)}
                  disabled={loadingList || filteredRows.length === 0}
                  style={{ flexShrink: 0, width: "fit-content", whiteSpace: "nowrap", minWidth: "110px" }}
                >
                  Export CSV
                </GlobalButton>
              </div>
            </div>
            {isAdminRoute ? (
              <div className="don-bulk-toolbar-wrap" style={{ marginBottom: "1rem" }}>
                <div className="don-bulk-toolbar" aria-label="Donation verification and certificate template">
                  <div className="don-bulk-toolbar__start">
                    <span
                      className={`don-bulk-toolbar__count${
                        selectedDonationIds.size === 0 ? " don-bulk-toolbar__count--idle" : ""
                      }`}
                    >
                      {selectedDonationIds.size > 0 ? selectedDonationIds.size : "—"}
                    </span>
                    <span className="don-bulk-toolbar__tag">Template</span>
                    <GlobalSelectField
                      id="don-verify-cert-template"
                      label="Certificate template"
                      value={certTemplatePick}
                      onChange={setCertTemplatePick}
                      placeholderOption="Choose template…"
                      options={certTemplateOptions}
                      disabled={bulkMarking || selectedDonationIds.size === 0}
                      classNames={{
                        label: "don-bulk-toolbar__sr",
                        fieldWrap: "don-bulk-toolbar__select",
                      }}
                    />
                  </div>
                  <div className="don-bulk-toolbar__end">
                    <GlobalButton
                      type="button"
                      variant="primary"
                      size="sm"
                      disabled={!selectedDonationIds.size || bulkMarking || !certTemplatePick}
                      onClick={() => runBulkDonationVerified(true)}
                      title="Verify selected donations and assign certificate template"
                    >
                      Approve & Apply
                    </GlobalButton>
                    <GlobalButton
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={!selectedDonationIds.size || bulkMarking}
                      onClick={() => runBulkDonationVerified(false)}
                    >
                      Unverify
                    </GlobalButton>
                    <GlobalButton
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={!selectedDonationIds.size}
                      onClick={() => setSelectedDonationIds(new Set())}
                    >
                      Clear
                    </GlobalButton>
                  </div>
                </div>
              </div>
            ) : null}
            {loadingList ? <p className="ub-loading">Loading donations…</p> : (
              <div className="ub-table-section">
                <GlobalDataTable wrapClassName="ub-table-wrap" tableClassName="ub-table" tableAriaLabel="Donations list" headers={tableHeaders}>
                  {filteredRows.length === 0 ? (
                    <tr><td colSpan={Math.max(visibleDataColumns.length + (isAdminRoute ? 2 : 1), 1)} style={{ textAlign: "center", padding: "2rem", color: "#6b7280" }}>No donations match filters.</td></tr>
                  ) : filteredRows.map((row) => (
                    <tr key={row.donation_id}>
                      {isAdminRoute ? (
                        <td className="checkbox-cell">
                          <input
                            type="checkbox"
                            checked={selectedDonationIds.has(String(row.donation_id))}
                            onChange={(e) => toggleDonationSelection(row.donation_id, e.target.checked)}
                          />
                        </td>
                      ) : null}
                      {visibleDataColumns.map((col) => (
                        <td key={col.id}>
                          {col.id === "donation_amount" ? formatMoney(row.donation_amount)
                            : col.id === "donation_date" ? formatDateCell(row.donation_date)
                            : col.id === "donation_verified" ? (
                              <GlobalBadge
                                active={Number(row?.donation_verified) === 1}
                                activeText="Verified"
                                inactiveText="Not verified"
                              />
                            )
                            : col.id === "certificate_template_name" ? (row.certificate_template_name ?? "—")
                            : col.id === "payment_type" ? (row.payment_type === "online" ? "Online" : "Offline")
                            : col.id === "payment_reference" ? (row.payment_transaction_id || "—")
                            : col.id === "payment_verified" ? (
                              <GlobalBadge
                                active={Number(row?.payment_verified) === 1}
                                activeText="Verified"
                                inactiveText="Not verified"
                              />
                            ) : row[col.id] ?? "—"}
                        </td>
                      ))}
                      <td className="ub-table__col-actions">
                        <div className="ub-table-actions">
                          <GlobalTableActionButton action="edit" variant="ghost" onClick={() => onRequestEdit(row)} />
                          {isStaffDonationTracking ? null : (
                            <>
                              {isPendingRow(row) ? (
                                <GlobalTableActionButton action="approve" variant="ghost" onClick={() => openConfirm(row, "approve")} />
                              ) : null}
                              {!isCancelledRow(row) ? (
                                <GlobalTableActionButton action="delete" variant="ghost" onClick={() => openConfirm(row, "delete")} />
                              ) : null}
                              <GlobalTableActionButton
                                action="receipt"
                                variant="ghost"
                                loading={downloadingReceiptId === row.donation_id}
                                onClick={() => onDownloadReceipt(row)}
                              />
                              <GlobalTableActionButton
                                action="download80g"
                                variant="ghost"
                                loading={downloading80gId === row.donation_id}
                                onClick={() => onDownload80g(row)}
                              />
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </GlobalDataTable>
              </div>
            )}
          </div>
          ) : null}

          {showEquipmentTrackingEmbed ? (
            <div id="equipment-tracking-embed" className="user-builder-panel" style={{ scrollMarginTop: "2rem" }}>
              <DonorEquipmentListPage embedMode="donations-tracking" onEquipmentDataChanged={handleEquipmentDataChanged} />
            </div>
          ) : null}

          {showDonationMasters ? (
            <div
              id="donation-masters"
              className="user-builder-panel"
              style={{ scrollMarginTop: "2rem", marginTop: "1rem" }}
            >
              <div className="user-builder-panel__head">
                <h3 className="user-builder-panel__title">
                  Master Table Management
                </h3>
                <p className="user-builder-panel__hint">
                  Configure donation purpose masters used across donation entry and
                  reports.
                </p>
              </div>
              <DonationPurposesMaster onDirectoryChange={loadLookups} />
            </div>
          ) : null}
        </div>
        <GlobalQuickJump
          asideAriaLabel="Donations page sections"
          items={[
            { key: "stats", label: "Summary", targetId: "donation-stats" },
            ...(showDonationForm ? [{ key: "form", label: "Add / edit", targetId: "donation-form" }] : []),
            ...(showEquipmentCollectEmbed ? [{ key: "equipment", label: "Equipment", targetId: "equipment-donations-embed" }] : []),
            ...(showDonationTable ? [{ key: "list", label: "Table", targetId: "donation-list" }] : []),
            ...(showEquipmentTrackingEmbed ? [{ key: "equipment", label: "Equipment", targetId: "equipment-tracking-embed" }] : []),
            ...(showDonationMasters
              ? [{ key: "masters", label: "Master tables", targetId: "donation-masters" }]
              : []),
          ]}
          activeKey={activeQuickJump}
          onActiveChange={setActiveQuickJump}
        />
      </div>

      <GlobalModal open={showConfirmModal} onClose={() => !isConfirming && setShowConfirmModal(false)} title={confirmType === "approve" ? "Approve donation" : "Delete donation"}
        footer={<div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
          <GlobalButton onClick={() => setShowConfirmModal(false)} disabled={isConfirming}>Back</GlobalButton>
          <GlobalButton variant={confirmType === "delete" ? "danger" : "primary"} onClick={handleConfirm} disabled={isConfirming}>
            {isConfirming ? "Working…" : confirmType === "approve" ? "Approve" : "Confirm delete"}
          </GlobalButton>
        </div>}>
        <p>{confirmType === "approve" ? "Approve this offline donation?" : "Delete this donation?"} <b>{donationToHandle?.donation_receipt_no}</b></p>
      </GlobalModal>
    </div>
  );
}
